import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import CalendarPanel, {
  CALENDAR_COLLAPSED_KEY,
  CALENDAR_SELECTED_DATE_KEY,
  startOfMonth,
  storedCalendarDate,
} from "@/components/calendar/CalendarPanel/CalendarPanel";
import {
  CALENDAR_DAY_SORT_KEY,
  CalendarDayTaskList,
  useDaySortMode,
} from "@/components/calendar/CalendarDayTasks/CalendarDayTasks";
import { trpc } from "@/lib/trpc";
import { parseDateKey, todayKey } from "@/lib/time";

function selectedDateFromUrl() {
  if (typeof window === "undefined") return storedCalendarDate(CALENDAR_SELECTED_DATE_KEY);
  return new URLSearchParams(window.location.search).get("day") || storedCalendarDate(CALENDAR_SELECTED_DATE_KEY);
}

export default function Calendar() {
  const [, setLocation] = useLocation();
  const [selectedDate, setSelectedDate] = useState(() => selectedDateFromUrl());
  const [cursor, setCursor] = useState(() => startOfMonth(parseDateKey(selectedDateFromUrl())));
  const [hasExplicitDay] = useState(
    () => typeof window !== "undefined" && new URLSearchParams(window.location.search).has("day"),
  );
  const [calendarCollapsed, setCalendarCollapsed] = useState(() => {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(CALENDAR_COLLAPSED_KEY) === "true";
  });
  const { sortMode, handleSortChange } = useDaySortMode(CALENDAR_DAY_SORT_KEY);
  const rememberLastDayQuery = trpc.preferences.rememberLastDay.useQuery();
  const rememberLastDay = rememberLastDayQuery.data?.calendar ?? true;
  const today = todayKey();

  useEffect(() => {
    if (!rememberLastDayQuery.data || rememberLastDayQuery.data.calendar) return;
    if (hasExplicitDay) return;
    setSelectedDate(today);
    setCursor(startOfMonth(parseDateKey(today)));
  }, [hasExplicitDay, rememberLastDayQuery.data, today]);

  useEffect(() => {
    if (typeof localStorage === "undefined" || !rememberLastDay) return;
    localStorage.setItem(CALENDAR_SELECTED_DATE_KEY, selectedDate);
  }, [rememberLastDay, selectedDate]);

  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(CALENDAR_COLLAPSED_KEY, String(calendarCollapsed));
  }, [calendarCollapsed]);

  useEffect(() => {
    setLocation(`/calendar?day=${selectedDate}`, { replace: true });
  }, [selectedDate, setLocation]);

  return (
    <AppShell title="Calendar">
      <div className="container max-w-3xl py-8 sm:py-12 space-y-8">
        <CalendarPanel
          selectedDate={selectedDate}
          cursor={cursor}
          collapsed={calendarCollapsed}
          onSelectedDateChange={setSelectedDate}
          onCursorChange={setCursor}
          onCollapsedChange={setCalendarCollapsed}
        />
        <CalendarDayTaskList
          date={selectedDate}
          label={selectedDate}
          sortMode={sortMode}
          onSortChange={handleSortChange}
        />
      </div>
    </AppShell>
  );
}
